/**
 * CORE-611 — global friend/DM presence over Agape.ChatApi's SignalR hub,
 * replacing Stream's `queryUsers({ presence: true })` + `user.presence.changed`.
 *
 * Unlike {@link ChatApiChannel} (one connection per channel), presence is a
 * single app-wide concern, so this owns ONE shared, lazily-opened hub connection
 * that every presence consumer (sidebar dots, conversation list, DM thread
 * header) piggy-backs on. Subscriptions are ref-counted per user id so opening a
 * DM and the sidebar tracking the same friend share one server-side group
 * membership, and the last release tears it down.
 *
 * Hub contract (server: Agape.ChatApi/Hubs/ChatHub.cs):
 *   invoke  SubscribeToPresence(userIds[])   → string[] (subset currently online)
 *   invoke  UnsubscribeFromPresence(userIds[])
 *   event   UserPresenceChanged { userId, online }   — scoped to subscribers
 *
 * Ids here are BARE platform guids (the JWT `sub` / `AppUser.Id`), NOT the
 * `user_`-prefixed Stream form — the caller strips the prefix at the boundary.
 * The per-channel `PresenceChanged` event is a DISTINCT concern (channel
 * watchers) and is intentionally ignored here.
 *
 * Connection/URL/RU-fallback logic mirrors {@link ChatApiChannel} exactly so
 * presence rides the same edge decisions as the chat thread it accompanies.
 */
import * as signalR from '@microsoft/signalr';
import { hubLogger } from '../../utils/hubConnectionOptions';
import { withHubTicket } from '../../utils/hubTicket';
import { storage } from '../../utils/storage';
import { CHAT_HUB_BASE_URL, chatHubRuProxy } from '../../constants/chat';
import { isChatRuActive, resolveHubUrl } from '../apiEndpoint';
import {
  chooseHubBase,
  markHubPathStalled,
  markHubProxyWorking,
} from './hubPath';
import { withDeadline } from './withDeadline';
import { api } from '../api';
import { startHubWithTimeout } from '../../utils/hubStartWithTimeout';

// Mirror ChatApiChannel's connect budget + RU stickiness (CORE-407/45): a hung
// direct hub socket on an RU ISP never rejects, so a timeout — not an error —
// is what triggers the proxy fallback, and once the proxy works we stay on it.
const HUB_CONNECT_TIMEOUT_MS = 10_000;
// CORE-1020 — липкость и память о вставшем пути общие с каналом переписки
// (`hubPath`): сокеты разные, путь один, и «этот путь встал» — свойство пути.
/** Текущее соединение поднято через РФ-прокси. */
let usingProxy = false;

/**
 * CORE-1020 — сколько ждать ответа хаба на подписку.
 *
 * Ниже стоит откат на REST — и он не работал: без потолка `await` на вставшем
 * туннеле не завершался никогда, до `catch` дело не доходило, и зелёные точки
 * оставались вчерашними навсегда. Запасной путь, который нельзя достичь, — это
 * не запасной путь.
 */
const PRESENCE_INVOKE_TIMEOUT_MS = 8_000;

/** Restart backoff after a terminal close: 1s, 2s, 4s … capped at 30 s. Kept
 *  local rather than imported from ChatApiChannel — presence has no other reason
 *  to depend on the channel module, and the two are free to diverge. */
function restartDelayMs(previousAttempt: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.min(previousAttempt, 5));
}

type Listener = () => void;

/**
 * CORE-621 — a new message somewhere the caller is a member. Fanned to every one
 * of the caller's connections by the server (SignalR user routing), so a client
 * gets it WITHOUT watching the channel. Drives the foreground new-message chime /
 * community toast (MessageNotifier) that used to ride the GetStream websocket.
 */
export interface MessageNotifyEvent {
  /** ChatApi channel key, e.g. `dm-a-b`, `master-circle-5`, `ai_sophia`. */
  channelKey: string;
  /** Author of the message — a bare app-user guid (never the caller's own). */
  senderId: string;
  messageId: number;
}
type MessageListener = (e: MessageNotifyEvent) => void;

/**
 * CORE-1654 / CORE-1688 — the master changed a community's discussion list
 * (new topic, rename, list pin, group, delete). Sent by the server to every
 * member of that community personally, so a client gets it without watching
 * the channel. Carries no rows: the panel re-reads the list.
 */
export interface DiscussionsChangedEvent {
  /** Whose community changed; null when the server sent no id. */
  masterProfileId: number | null;
}
type DiscussionsListener = (e: DiscussionsChangedEvent) => void;

/**
 * CORE-1753, возврат QA 23.10 — соединение (снова) поднялось.
 *
 * `MessageNotify` сервер шлёт только живым соединениям и не копит. Всё, что
 * пришло, пока сокета не было, — при входе, при переподключении, после фона в
 * приложении, — потеряно, и узнать об этом можно только перечитав. На DEV это
 * было приветствие Агапеллы при входе: оно уходит на ~2,2 с, а сокет
 * открывается на ~2,3 с, и отметка ждала опроса раз в 30 с (6 входов из 6).
 * Касается любого отправителя, а не только Агапеллы.
 */
type ConnectedListener = () => void;

class ChatPresenceHub {
  private conn: signalR.HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  /** bare userId → number of live callers interested in it. */
  private readonly refs = new Map<string, number>();
  /** bare userIds currently known online (⊆ refs.keys()). */
  private readonly online = new Set<string>();
  private readonly listeners = new Set<Listener>();
  /** CORE-621 — global new-message subscribers (MessageNotifier). */
  private readonly messageListeners = new Set<MessageListener>();
  /** CORE-1688 — discussion-list subscribers (MasterCommunityChatPanel). */
  private readonly discussionsListeners = new Set<DiscussionsListener>();
  /** CORE-1753 — кому сказать, что соединение поднялось (счётчики непрочитанного). */
  private readonly connectedListeners = new Set<ConnectedListener>();
  /** Self-healing restart after the connection closed for good (see onclose). */
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private restartAttempt = 0;

  /** Snapshot copy of the bare userIds currently online. */
  getOnline(): Set<string> {
    return new Set(this.online);
  }

  /** React to any change in the online set. Returns an unlisten fn. */
  addListener(l: Listener): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  private emit() {
    this.listeners.forEach((l) => {
      try {
        l();
      } catch {
        /* a listener throwing must not wedge the others */
      }
    });
  }

  /**
   * CORE-621 — subscribe to the global new-message stream. No per-channel watch:
   * the server user-routes `MessageNotify` to all the caller's connections, so we
   * only need the shared hub connection alive. Opens it if not already up (a
   * MessageNotifier mounted app-wide keeps one persistent connection while
   * authenticated). Best-effort — a hub hiccup just drops the chime, never throws.
   * Returns an unlisten fn.
   */
  addMessageListener(l: MessageListener): () => void {
    this.messageListeners.add(l);
    this.ensureConnected().catch(() => {
      /* degraded — reconnect (withAutomaticReconnect) resumes delivery */
    });
    return () => {
      this.messageListeners.delete(l);
    };
  }

  /**
   * CORE-1688 — subscribe to «the discussion list of some community changed».
   * Same transport and lifetime rules as {@link addMessageListener}.
   */
  addDiscussionsListener(l: DiscussionsListener): () => void {
    this.discussionsListeners.add(l);
    this.ensureConnected().catch(() => {
      /* degraded — reconnect resumes delivery; the list still loads on mount */
    });
    return () => {
      this.discussionsListeners.delete(l);
    };
  }

  /**
   * CORE-1753 — «соединение поднялось: что было без него, потеряно». Зовётся
   * после каждого подключения — первого, автоматического переподключения и
   * пересборки после окончательного закрытия. Соединение не открывает: его
   * держат подписчики сообщений. Returns an unlisten fn.
   */
  addConnectedListener(l: ConnectedListener): () => void {
    this.connectedListeners.add(l);
    return () => {
      this.connectedListeners.delete(l);
    };
  }

  private emitConnected() {
    this.connectedListeners.forEach((l) => {
      try {
        l();
      } catch {
        /* a listener throwing must not wedge the others */
      }
    });
  }

  private emitDiscussions(e: DiscussionsChangedEvent) {
    this.discussionsListeners.forEach((l) => {
      try {
        l(e);
      } catch {
        /* a listener throwing must not wedge the others */
      }
    });
  }

  private emitMessage(e: MessageNotifyEvent) {
    this.messageListeners.forEach((l) => {
      try {
        l(e);
      } catch {
        /* a listener throwing must not wedge the others */
      }
    });
  }

  /**
   * Track presence for `userIds` (bare guids). Ref-counted: only ids new to the
   * hub are sent to the server, and the returned fn releases exactly this
   * caller's interest. Best-effort — if the hub can't connect we degrade to
   * "nobody online" (same failure shape as Stream presence timing out).
   */
  async subscribe(userIds: string[]): Promise<() => void> {
    const ids = Array.from(new Set(userIds.filter(Boolean)));
    if (ids.length === 0) return () => {};

    const fresh: string[] = [];
    ids.forEach((id) => {
      const n = this.refs.get(id) ?? 0;
      if (n === 0) fresh.push(id);
      this.refs.set(id, n + 1);
    });

    try {
      await this.ensureConnected();
      if (fresh.length) await this.subscribeIds(fresh);
    } catch {
      /* degraded — refs stay so a later reconnect re-subscribes them */
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.release(ids);
    };
  }

  private release(ids: string[]) {
    const gone: string[] = [];
    ids.forEach((id) => {
      const n = this.refs.get(id) ?? 0;
      if (n <= 1) {
        this.refs.delete(id);
        if (this.online.delete(id)) this.emit();
        gone.push(id);
      } else {
        this.refs.set(id, n - 1);
      }
    });
    if (gone.length && this.conn?.state === signalR.HubConnectionState.Connected) {
      this.conn.invoke('UnsubscribeFromPresence', gone).catch(() => {});
    }
  }

  /** Seed the online set for `ids`: hub snapshot first, REST as a backstop. */
  private async subscribeIds(ids: string[]) {
    let snapshot: string[] = [];
    if (this.conn?.state === signalR.HubConnectionState.Connected) {
      try {
        snapshot = (await withDeadline(
          this.conn.invoke<string[]>('SubscribeToPresence', ids),
          PRESENCE_INVOKE_TIMEOUT_MS,
          'SubscribeToPresence',
        )) ?? [];
      } catch {
        // Поднявшийся и замолчавший сокет — вставший туннель; помечаем путь,
        // чтобы следующее подключение (в том числе канала переписки) его не
        // выбирало, и уходим на REST.
        markHubPathStalled(usingProxy);
      }
    }
    if (snapshot.length === 0) {
      snapshot = await api.getChatPresence(ids);
    }
    let changed = false;
    snapshot.forEach((id) => {
      // Guard against a stale REST hit for an id already released.
      if (this.refs.has(id) && !this.online.has(id)) {
        this.online.add(id);
        changed = true;
      }
    });
    if (changed) this.emit();
  }

  private handlePresence = (userId: string, isOnline: boolean) => {
    if (!this.refs.has(userId)) return; // not something any caller asked about
    if (isOnline && !this.online.has(userId)) {
      this.online.add(userId);
      this.emit();
    } else if (!isOnline && this.online.has(userId)) {
      this.online.delete(userId);
      this.emit();
    }
  };

  private buildConn(hubBase: string): signalR.HubConnection {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(`${hubBase}/hubs/chat`), {
        accessTokenFactory: withHubTicket(() => storage.getToken()), // CORE-1736
        logger: hubLogger, // CORE-1364 — no JWT in the console
      })
      .withAutomaticReconnect()
      .build();

    conn.on('UserPresenceChanged', (p: any) => {
      const uid = String(p?.userId ?? p?.UserId ?? '');
      if (uid) this.handlePresence(uid, !!(p?.online ?? p?.Online));
    });

    // CORE-621 — global new-message notify (user-routed by the server; no group
    // subscription needed). Feeds the foreground chime / community toast.
    conn.on('MessageNotify', (p: any) => {
      const channelKey = String(p?.channelKey ?? p?.ChannelKey ?? '');
      const senderId = String(p?.senderId ?? p?.SenderId ?? '');
      const messageId = Number(p?.messageId ?? p?.MessageId ?? 0);
      if (channelKey && senderId) this.emitMessage({ channelKey, senderId, messageId });
    });

    // CORE-1688 — the discussion list changed; user-routed like MessageNotify.
    // The payload shape is the server's: an object with masterProfileId, or a
    // bare id — anything else still fires, just without the id.
    conn.on('DiscussionsChanged', (p: any) => {
      const raw = typeof p === 'number' ? p : p?.masterProfileId ?? p?.MasterProfileId;
      const id = Number(raw);
      this.emitDiscussions({ masterProfileId: Number.isFinite(id) && raw != null ? id : null });
    });

    // On reconnect the group memberships are gone and we missed any flips while
    // down — re-subscribe every tracked id and reseed from the fresh snapshot.
    conn.onreconnected(() => {
      const all = Array.from(this.refs.keys());
      const wasOnline = this.online.size > 0;
      this.online.clear();
      if (wasOnline) this.emit();
      if (all.length) this.subscribeIds(all).catch(() => {});
      // CORE-1753 — пока сокета не было, `MessageNotify` уходили в никуда.
      this.emitConnected();
    });

    // `withAutomaticReconnect()` here uses the DEFAULT policy, which gives up
    // after four tries (~42 s). Past that the socket closed for good and nothing
    // brought it back: presence froze at whatever it last knew, and the
    // MessageNotify feed behind the in-app chime went silent — until the app was
    // restarted. ChatApiChannel already self-heals for exactly this reason.
    // Guarded: `onclose` exists on every real HubConnection, but a test double
    // need not provide it, and a missing handler must not take the hub down.
    conn.onclose?.(() => this.scheduleRestart());

    return conn;
  }

  /** Rebuild the connection after it closed for good, with capped backoff. */
  private scheduleRestart(): void {
    if (this.restartTimer) return;
    // Nobody is listening for presence or messages — let it stay down; the next
    // subscribe()/addMessageListener() opens a fresh connection on demand.
    if (this.refs.size === 0 && this.messageListeners.size === 0 && this.discussionsListeners.size === 0) return;
    const delay = restartDelayMs(this.restartAttempt);
    this.restartAttempt += 1;
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      this.ensureConnected()
        .then(() => {
          this.restartAttempt = 0;
          // The reconnect handler above only fires for the automatic policy's
          // own recovery, not for a connection we rebuilt ourselves — so reseed
          // the tracked ids here too.
          const all = Array.from(this.refs.keys());
          if (all.length) this.subscribeIds(all).catch(() => {});
        })
        .catch(() => this.scheduleRestart());
    }, delay);
  }

  private ensureConnected(): Promise<void> {
    if (this.conn && this.conn.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }
    if (!this.startPromise) {
      this.startPromise = this.doConnect().finally(() => {
        this.startPromise = null;
      });
    }
    return this.startPromise;
  }

  private async doConnect(): Promise<void> {
    const primary = CHAT_HUB_BASE_URL;
    // Resolved per connect, not per module load — see chatHubRuProxy().
    const proxy = chatHubRuProxy();
    const choice = chooseHubBase(primary, proxy, isChatRuActive());
    const { base, canFallback } = choice;
    usingProxy = choice.usingProxy;

    this.conn = this.buildConn(base);
    try {
      await this.startWithTimeout(this.conn, HUB_CONNECT_TIMEOUT_MS);
    } catch (err) {
      if (!canFallback || base === proxy) throw err;
      try {
        await this.conn.stop();
      } catch {
        /* abort the timed-out start */
      }
      this.conn = this.buildConn(proxy);
      usingProxy = true;
      await this.conn.start();
      markHubProxyWorking();
    }
    // CORE-1753 — первое подключение и пересборка после закрытия тоже
    // оставляют окно, в котором `MessageNotify` терялись.
    this.emitConnected();
  }

  /**
   * CORE-1202 — гонка переехала в `startHubWithTimeout`.
   *
   * Здешняя копия уже гасила таймер, но от глобального хука это не спасало:
   * `.finally()` возвращает НОВОЕ обещание, и когда гонку забирал таймаут,
   * поздний отказ `start()` (отмена хендшейка нашим же `stop()`) оставался без
   * подписчика ровно так же, как в двух других копиях.
   */
  private startWithTimeout(conn: signalR.HubConnection, ms: number): Promise<void> {
    return startHubWithTimeout(conn, ms, 'presence hub');
  }
}

/** App-wide singleton — every presence consumer shares this one connection. */
export const chatPresenceHub = new ChatPresenceHub();
